import Trix from 'trix'

// Runtime state belongs to this mounted field only, never to its PHP config or
// a global current-editor slot. Alpine calls destroy() when Livewire removes it.
function trixField() {
  let root, element, group, observer, pending, config, controlledToolbar, toolbarDisplay
  let lastValue, applyingValue = false
  let removeListeners = () => {}
  let disposed = false

  const input = () => root.querySelector(':scope > [x-ref="input"]')
  const currentValue = () => {
    const hidden = input()
    return hidden?._x_model ? String(hidden._x_model.get() ?? '') : hidden?.value
  }
  const surface = () => root.querySelector(':scope > [x-ref="surface"]')
  const currentElement = () => surface()?.querySelector(':scope > [x-ref="trix"]')
  const inert = () => root.dataset.trixInert === 'true'
  const editable = () => !disposed && root.isConnected && element?.isConnected
    && currentElement() === element && !inert()
    && !element.disabled && element.getAttribute('contenteditable') !== 'false'
  const invalidate = () => { pending = undefined }
  const syncInput = () => {
    if (!disposed && !applyingValue && root.isConnected && currentElement() === element && !inert()
      && input()?.value === lastValue) {
      input()?.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  const activate = event => {
    if (event.target !== element || !editable()) return
    const button = event.invokingElement
    if (!group?.contains(button) || button.disabled
      || button.dataset.trixAction !== event.actionName) return

    const editor = element.editor
    const origin = element
    const range = [...editor.getSelectedRange()]
    const document = editor.getDocument()
    const request = {}
    pending = request
    const valid = () => pending === request && editable() && element === origin
      && editor.getDocument().isEqualTo(document)
      && config === root.dataset.trixToolbarActions
      && currentValue() === lastValue
    const restoreFocus = () => {
      origin.focus()
      editor.setSelectedRange(range)
    }

    element.dispatchEvent(new CustomEvent('resourcerer-trix-action', {
      bubbles: true,
      detail: {
        name: button.dataset.resourcererTrixAction,
        editor: origin,
        insertText(text) {
          if (typeof text !== 'string' || text.length === 0 || !valid()) return false
          invalidate()
          restoreFocus()
          editor.recordUndoEntry('Insert text')
          editor.insertString(text)
          return true
        },
        cancel() {
          if (!valid()) return false
          invalidate()
          restoreFocus()
          return true
        },
      },
    }))
  }

  const refresh = () => {
    if (disposed) return
    const next = currentElement()
    if (next !== element) {
      removeListeners()
      group?.remove()
      group = undefined
      config = undefined
      invalidate()
      element = next
      lastValue = input()?.value
      if (!element) return
      const onInitialize = () => refresh()
      const onChange = () => {
        lastValue = input()?.value
        if (applyingValue) return
        invalidate()
        syncInput()
      }
      element.addEventListener('trix-initialize', onInitialize)
      element.addEventListener('trix-change', onChange)
      element.addEventListener('blur', syncInput)
      element.addEventListener('trix-action-invoke', activate)
      removeListeners = () => {
        next.removeEventListener('trix-initialize', onInitialize)
        next.removeEventListener('trix-change', onChange)
        next.removeEventListener('blur', syncInput)
        next.removeEventListener('trix-action-invoke', activate)
      }
    }
    if (!element?.editor || !element.toolbarElement) return
    // Livewire reconciles its bound model before morphing attributes. An older
    // response may still write a stale value attribute over newer local input.
    // Read Alpine's current binding, not that attribute, and restore the input
    // without emitting an event or overwriting the reconciled model.
    const hidden = input()
    const value = currentValue()
    if (hidden && hidden.value !== value) hidden.value = value
    if (hidden && value !== lastValue) {
      invalidate()
      applyingValue = true
      try {
        element.editor.loadHTML(value)
      } finally {
        // Trix may normalize HTML; the bound reset itself is still unchanged.
        lastValue = currentValue()
        applyingValue = false
      }
    }
    const toolbar = element.toolbarElement
    if (toolbar !== controlledToolbar) {
      controlledToolbar = toolbar
      toolbarDisplay = toolbar.style.display
    }
    // contenteditable=false alone does not stop native toolbar API mutations.
    toolbar.hidden = inert()
    toolbar.inert = inert()
    // Trix's author stylesheet sets display:block, overriding native [hidden].
    const display = inert() ? 'none' : toolbarDisplay
    if (toolbar.style.display !== display) toolbar.style.display = display
    const contenteditable = inert() ? 'false' : 'true'
    if (element.getAttribute('contenteditable') !== contenteditable) {
      element.setAttribute('contenteditable', contenteditable)
    }
    if (inert()) element.setAttribute('aria-disabled', 'true')
    else element.removeAttribute('aria-disabled')
    if (inert()) invalidate()

    const nextConfig = root.dataset.trixToolbarActions
    if (config === nextConfig && (!group || group.parentElement?.isConnected)) return
    invalidate()
    config = nextConfig
    group?.remove()
    group = undefined
    const actions = JSON.parse(config || '[]')
    if (!actions.length) return
    group = document.createElement('span')
    group.className = 'trix-button-group'
    group.dataset.resourcererTrixActions = ''
    for (const action of actions) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'trix-button'
      button.textContent = action.label
      button.dataset.trixAction = `x-resourcerer-${action.name}`
      button.dataset.resourcererTrixAction = action.name
      group.append(button)
    }
    // Trix 2's native external actions use mousedown. Native button keyboard
    // activation emits a click with detail=0; route it through the same API.
    group.addEventListener('click', event => {
      const button = event.target.closest('button')
      if (event.detail === 0 && button && !button.disabled && editable()) {
        button.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
      }
    })
    ;(toolbar.querySelector('.trix-button-row') || toolbar).append(group)
  }

  return {
    init() {
      root = this.$el
      refresh()
      // Observe server config/input and surface replacement, not Trix's content
      // changes. The ignored surface itself survives ordinary Livewire morphs.
      observer = new MutationObserver(records => {
        if (records.some(({ target }) => target === root || target === input() || target === surface())) refresh()
      })
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-trix-toolbar-actions', 'data-trix-inert', 'value'],
      })
    },
    destroy() {
      disposed = true
      observer?.disconnect()
      invalidate()
      removeListeners()
      group?.remove()
    },
  }
}

export default (Alpine) => {
  Alpine?.data('trixField', trixField)
  if (window.__resourcererTrixInitialized) return
  window.__resourcererTrixInitialized = true

  window.Trix = Trix

  document.addEventListener('trix-before-initialize', () => {
    Trix.config.blockAttributes.heading1 = {
      tagName: 'h2',
      terminal: true,
      breakOnReturn: true,
      group: false
    }
  })

  const initialized = new WeakSet()
  document.addEventListener('trix-initialize', (event) => {
    const editor = event.target
    if (!editor || initialized.has(editor)) return
    initialized.add(editor)
    // The Alpine field owns (and cleans up) its scoped synchronization.
    if (editor.closest('[x-data="trixField"]')) return
    editor.addEventListener('blur', () => {
      if (!editor.isConnected) return
      // Resolve lazily after morphs; field IDs include both host and binding.
      const inputId = editor.getAttribute('input')
      if (!inputId) return
      document.getElementById(inputId)?.dispatchEvent(new Event('input', { bubbles: true }))
    })
  })
}
